// import create from 'zustand'
import create from 'zustand'
import { v4 as uuidv4 } from 'uuid'

// Local storage key for persisting upload data
const STORAGE_KEY = 'lidya_uploads'

// Helper functions for localStorage
const saveToLocalStorage = (uploads) => {
  try {
    const persistedUploads = {}

    // Only persist uploads that are in progress or interrupted
    Object.entries(uploads).forEach(([id, upload]) => {
      if (upload.status === 'uploading' || upload.status === 'interrupted' || upload.status === 'paused') {
        persistedUploads[id] = upload
      }
    })

    localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedUploads))
  } catch (error) {
    console.error('Error saving uploads to localStorage:', error)
  }
}

const loadFromLocalStorage = () => {
  try {
    const savedUploads = localStorage.getItem(STORAGE_KEY)
    if (savedUploads) {
      const parsed = JSON.parse(savedUploads)

      // Mark all loaded uploads as interrupted
      Object.values(parsed).forEach((upload) => {
        upload.status = 'interrupted'
      })

      return parsed
    }
  } catch (error) {
    console.error('Error loading uploads from localStorage:', error)
  }
  return {}
}

// Define a more straightforward store with simpler functions
export const useUploadTrackerStore = create((set, get) => ({
  // Store state
  uploads: loadFromLocalStorage(),
  totalUploads: Object.keys(loadFromLocalStorage()).length,
  activeUploads: 0,
  completedUploads: 0,
  failedUploads: 0,
  interruptedUploads: Object.keys(loadFromLocalStorage()).length,
  pausedUploads: 0,

  // Basic actions
  addUpload: function (fileData) {
    const id = fileData.id || uuidv4()
    set((state) => {
      const newState = {
        uploads: {
          ...state.uploads,
          [id]: {
            id,
            filename: fileData.filename,
            progress: 0,
            timeRemaining: 0,
            status: 'uploading',
            totalBytes: fileData.totalBytes || 0,
            timestamp: Date.now(),
            fileKey: fileData.fileKey || null, // Store a unique key for the file
            metadata: fileData.metadata || {}, // Store additional metadata
          },
        },
        totalUploads: state.totalUploads + 1,
        activeUploads: state.activeUploads + 1,
      }

      // Save to localStorage
      saveToLocalStorage(newState.uploads)

      return newState
    })
    return id
  },

  updateUploadProgress: function (id, progress, timeRemaining) {
    set((state) => {
      if (!state.uploads[id]) return state

      const updatedUploads = {
        ...state.uploads,
        [id]: {
          ...state.uploads[id],
          progress,
          timeRemaining,
        },
      }

      // Save to localStorage
      saveToLocalStorage(updatedUploads)

      return {
        uploads: updatedUploads,
      }
    })
  },

  completeUpload: function (id) {
    set((state) => {
      if (!state.uploads[id]) return state

      const updatedUploads = {
        ...state.uploads,
        [id]: {
          ...state.uploads[id],
          progress: 100,
          timeRemaining: 0,
          status: 'completed',
        },
      }

      // Save to localStorage after updating status
      saveToLocalStorage(updatedUploads)

      return {
        uploads: updatedUploads,
        activeUploads: state.activeUploads - 1,
        completedUploads: state.completedUploads + 1,
        interruptedUploads:
          state.interruptedUploads > 0
            ? state.uploads[id].status === 'interrupted'
              ? state.interruptedUploads - 1
              : state.interruptedUploads
            : 0,
        pausedUploads:
          state.pausedUploads > 0
            ? state.uploads[id].status === 'paused'
              ? state.pausedUploads - 1
              : state.pausedUploads
            : 0,
      }
    })
  },

  failUpload: function (id, error) {
    set((state) => {
      if (!state.uploads[id]) return state

      const updatedUploads = {
        ...state.uploads,
        [id]: {
          ...state.uploads[id],
          status: 'failed',
          error: error || 'Upload failed',
        },
      }

      // Save to localStorage
      saveToLocalStorage(updatedUploads)

      return {
        uploads: updatedUploads,
        activeUploads: state.activeUploads - 1,
        failedUploads: state.failedUploads + 1,
        interruptedUploads:
          state.interruptedUploads > 0
            ? state.uploads[id].status === 'interrupted'
              ? state.interruptedUploads - 1
              : state.interruptedUploads
            : 0,
        pausedUploads:
          state.pausedUploads > 0
            ? state.uploads[id].status === 'paused'
              ? state.pausedUploads - 1
              : state.pausedUploads
            : 0,
      }
    })
  },

  cancelUpload: function (id) {
    set((state) => {
      if (!state.uploads[id]) return state

      const updatedUploads = {
        ...state.uploads,
        [id]: {
          ...state.uploads[id],
          status: 'cancelled',
        },
      }

      // Save to localStorage
      saveToLocalStorage(updatedUploads)

      return {
        uploads: updatedUploads,
        activeUploads: state.activeUploads - 1,
        interruptedUploads:
          state.interruptedUploads > 0
            ? state.uploads[id].status === 'interrupted'
              ? state.interruptedUploads - 1
              : state.interruptedUploads
            : 0,
        pausedUploads:
          state.pausedUploads > 0
            ? state.uploads[id].status === 'paused'
              ? state.pausedUploads - 1
              : state.pausedUploads
            : 0,
      }
    })
  },

  interruptUpload: function (id) {
    set((state) => {
      if (!state.uploads[id]) return state

      const updatedUploads = {
        ...state.uploads,
        [id]: {
          ...state.uploads[id],
          status: 'interrupted',
        },
      }

      // Save to localStorage
      saveToLocalStorage(updatedUploads)

      return {
        uploads: updatedUploads,
        activeUploads: state.activeUploads - 1,
        interruptedUploads: state.interruptedUploads + 1,
        pausedUploads:
          state.pausedUploads > 0
            ? state.uploads[id].status === 'paused'
              ? state.pausedUploads - 1
              : state.pausedUploads
            : 0,
      }
    })
  },

  pauseUpload: function (id) {
    set((state) => {
      if (!state.uploads[id]) return state

      const updatedUploads = {
        ...state.uploads,
        [id]: {
          ...state.uploads[id],
          status: 'paused',
          pausedAt: Date.now(),
        },
      }

      // Save to localStorage
      saveToLocalStorage(updatedUploads)

      return {
        uploads: updatedUploads,
        activeUploads: state.activeUploads - 1,
        pausedUploads: state.pausedUploads + 1,
      }
    })
  },

  resumePausedUpload: function (id) {
    set((state) => {
      if (!state.uploads[id]) return state

      const updatedUploads = {
        ...state.uploads,
        [id]: {
          ...state.uploads[id],
          status: 'uploading',
          resumedAt: Date.now(),
        },
      }

      // Save to localStorage
      saveToLocalStorage(updatedUploads)

      return {
        uploads: updatedUploads,
        activeUploads: state.activeUploads + 1,
        pausedUploads: state.pausedUploads - 1,
      }
    })
  },

  resumeUpload: function (id) {
    set((state) => {
      if (!state.uploads[id]) return state

      const updatedUploads = {
        ...state.uploads,
        [id]: {
          ...state.uploads[id],
          status: 'uploading',
        },
      }

      // Save to localStorage
      saveToLocalStorage(updatedUploads)

      return {
        uploads: updatedUploads,
        activeUploads: state.activeUploads + 1,
        interruptedUploads: state.interruptedUploads - 1,
      }
    })
  },

  updateUploadMetadata: function (id, metadataOrUpdater) {
    set((state) => {
      if (!state.uploads[id]) return state

      const currentMetadata = state.uploads[id].metadata || {}
      const updatedMetadata =
        typeof metadataOrUpdater === 'function'
          ? metadataOrUpdater(currentMetadata)
          : {
              ...currentMetadata,
              ...metadataOrUpdater,
            }

      const updatedUploads = {
        ...state.uploads,
        [id]: {
          ...state.uploads[id],
          metadata: updatedMetadata,
        },
      }

      // Save to localStorage
      saveToLocalStorage(updatedUploads)

      return {
        uploads: updatedUploads,
      }
    })
  },

  removeUpload: function (id) {
    set((state) => {
      const { [id]: uploadToRemove, ...remainingUploads } = state.uploads
      if (!uploadToRemove) return state

      // Save to localStorage after removing
      saveToLocalStorage(remainingUploads)

      let newState = {
        uploads: remainingUploads,
        totalUploads: state.totalUploads - 1,
      }

      if (uploadToRemove.status === 'completed') {
        newState.completedUploads = state.completedUploads - 1
      } else if (uploadToRemove.status === 'failed') {
        newState.failedUploads = state.failedUploads - 1
      } else if (uploadToRemove.status === 'uploading') {
        newState.activeUploads = state.activeUploads - 1
      } else if (uploadToRemove.status === 'interrupted') {
        newState.interruptedUploads = state.interruptedUploads - 1
      } else if (uploadToRemove.status === 'paused') {
        newState.pausedUploads = state.pausedUploads - 1
      }

      return newState
    })
  },

  clearFinishedUploads: function () {
    set((state) => {
      const activeAndInterruptedUploads = {}

      Object.entries(state.uploads).forEach(([id, upload]) => {
        if (upload.status === 'uploading' || upload.status === 'interrupted' || upload.status === 'paused') {
          activeAndInterruptedUploads[id] = upload
        }
      })

      // Save to localStorage
      saveToLocalStorage(activeAndInterruptedUploads)

      return {
        uploads: activeAndInterruptedUploads,
        totalUploads: state.activeUploads + state.interruptedUploads + state.pausedUploads,
        completedUploads: 0,
        failedUploads: 0,
      }
    })
  },

  clearAllUploads: function () {
    // Clear localStorage
    localStorage.removeItem(STORAGE_KEY)

    set({
      uploads: {},
      totalUploads: 0,
      activeUploads: 0,
      completedUploads: 0,
      failedUploads: 0,
      interruptedUploads: 0,
      pausedUploads: 0,
    })
  },

  // Helper to get interrupted uploads
  getInterruptedUploads: () => {
    return Object.entries(get().uploads)
      .filter(([_, upload]) => upload.status === 'interrupted')
      .map(([id, upload]) => upload)
  },

  // Helper to get paused uploads
  getPausedUploads: () => {
    return Object.entries(get().uploads)
      .filter(([_, upload]) => upload.status === 'paused')
      .map(([id, upload]) => upload)
  },

  clearInterruptedUploads: function () {
    set((state) => {
      const remainingUploads = {}
      let removedCount = 0
      Object.entries(state.uploads).forEach(([id, upload]) => {
        if (upload.status !== 'interrupted') {
          remainingUploads[id] = upload
        } else {
          removedCount++
        }
      })
      // Save to localStorage
      saveToLocalStorage(remainingUploads)
      return {
        uploads: remainingUploads,
        totalUploads: state.totalUploads - removedCount,
        interruptedUploads: 0,
      }
    })
  },
}))
